import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CanvasRenderer, CanvasTitleRenderer} from 'notebook/canvas/CanvasConstants';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import {StructuredTableDict} from 'notebook/documents/DocumentConstants';
import {HistoryState, IndexSlice} from 'notebook/ELNConstants';
import DocumentManagerComponent from 'notebook/mediocre/core-ui/DocumentManagerComponent';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import NoteEditorContainer from 'notebook/notes/core-ui/LazyNoteEditorContainer';
import {CidIdMap, PRINT_OPTION} from 'notebook/notes/NoteConstants';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {BioentityNote, EntryNote, Note} from 'notebook/util/NoteUtilConstants';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import RequestEntryTable from 'requests/RequestEntryTable';
import {ELN_SHOW_FORMULAS, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import licenses from 'user/Licenses';
import {assertNever, ElementKeyboardEvent} from 'util/Types';

import EntryNoteTitleBadge from './EntryNoteTitleBadge';

export interface NoteViewProps<NoteType> {
  afterEntry?: React.ReactNode;
  banner?: React.ReactNode;
  beforeEntry?: React.ReactNode;
  editorIcon?: JSX.Element;
  cidIdMap?: CidIdMap;
  documentEditorLock?: DocumentEditorLock;
  note: NoteType | null;
  historyManager: HistoryManager;
  historyResetCount?: number;
  title?: string;
  titleBadge?: React.ReactNode;
  updateTitle?: (title: string) => void;
  dirty?: boolean;
  locked: boolean;
  fileId: string;
  humanId?: string | null;
  templateVersionId?: number;
  templateUrl?: string;
  templateVersionTimestamp?: string;
  fileType: string;
  userReadOnlyMode?: boolean;
  fileTypeName?: string;
  fullScreen?: boolean;
  fullHeight?: boolean;
  onLoad?: () => {};
  restoreStateAndSelection: (state: HistoryState<NoteType>, selectionData: SelectionRestoreData) => void;
  printOptions?: Array<string>;
  disableFormatting?: boolean;
  disableMaximizedTables?: boolean;
  preview?: {followLink?: boolean; showOpenLink?: boolean};
  displayOptions?: VisibilityOptions;
  showTableOfContents: boolean;
  showTimestamps: boolean;
  spellCheck: boolean;
  printLayout: boolean;
  showPrintMargins: boolean;
  collaboratorIds?: Array<string>;
  indexSlice?: {start: number; end: number};
  permissions?: Permissions;
  lockedFromExternalChange?: boolean;
  minimalToolbar?: boolean;
  isConfig?: boolean;
  renderCanvas?: CanvasRenderer;
  renderCanvasTitle?: CanvasTitleRenderer;
  focusedTableProvenanceUuid?: string;
  focusedTableViewMode?: string;
  allowedStructuredTableTypes?: Array<string>;
  tablesToInsertOnCreation?: Array<StructuredTableDict>;
  customInsertableTables?: Array<string>;
  structuredTableMappings?: Array<StructuredTableMapping>;
  printHandler?: (format: PRINT_OPTION, indexSlice?: IndexSlice) => void;
  registerObjects: ((fileId: string, cidIdMap: CidIdMap) => void) | ((cidIdMap: CidIdMap) => void);
  getIdFromCid: (cid: string) => number | string | undefined;
  updateNote: (
    note: NoteType,
    opts?: {performTableEvaluation: boolean},
    structuredTableMappings?: Array<StructuredTableMapping>,
    updatedRelatedDocuments?: {[documentId: string]: Note},
    tableOpDiffMap?: TableOpDiffMap
  ) => void;
  parentType: string;
  viewer?: boolean;
  isVirtualizationEnabled?: boolean;
  hideTitle?: boolean;
  isViewingPublishedVersion?: boolean;
  tableNamespace?: string;
  disableSplitscreen?: boolean;
}

interface EntryNoteViewProps extends NoteViewProps<EntryNote> {
  isLatest: boolean;
  allowEdits: boolean;
}

interface State {
  cid: string;
}

interface EntryState extends State {
  historyManager: HistoryManager;
}

interface RenderProps {
  metadataForRestore: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData} | null;
  redoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  requestSelection: (selectionRestoreData: SelectionRestoreData) => void;
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: SelectionRestoreData) => boolean;
  storeState: (selectionData: {selection: SelectionRestoreData}) => void;
  undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  undoStoreState: () => void;
}

function mapStateToProps<NoteType>(
  state: BenchlingState,
  props: NoteViewProps<NoteType>
): {documentEditorLock?: DocumentEditorLock} {
  return DocumentEditorLockUtil.mapDocumentEditorLockStateToProps(state, props.fileId);
}

export const NoteView = connect(mapStateToProps)(
  class NoteView extends React.PureComponent<NoteViewProps<Note>, State> {
    static defaultProps: Partial<NoteViewProps<BioentityNote>> = {
      fullScreen: true,
    };

    override state: State = {
      cid: _.uniqueId('note-editor'),
    };

    override componentDidUpdate(prevProps: NoteViewProps<BioentityNote>, prevState: State): void {
      if (this.props.historyResetCount !== prevProps.historyResetCount) {
        this.props.historyManager.reset();
      }
    }

    getBannerMessage = (): React.ReactNode | undefined => {
      let result = this.props.banner;

      if (result == null && this.props.userReadOnlyMode && this.props.displayOptions) {
        result = (
          <DocumentBanner.ReadOnlyMode
            onExitReadOnlyMode={() => {
              const readOnlyOption = this.props.displayOptions?.getOption('elnReadOnlyMode');
              if (readOnlyOption) {
                readOnlyOption.set('visible', false);
              }
            }}
          />
        );
      }

      // Editor lock banner will only display if there is an active lock and the editor toolbar is not already
      // disabled for some other reason
      if (
        result == null &&
        this.props.documentEditorLock &&
        DocumentEditorLockUtil.isLockedInRealtime(this.props.documentEditorLock)
      ) {
        result = (
          <DocumentBanner.ResourceLocked
            userId={this.props.documentEditorLock.userId}
            docType={this.props.fileType}
          />
        );
      }

      return result;
    };

    override render(): JSX.Element {
      const isLockedToOtherClient = this.props.documentEditorLock
        ? DocumentEditorLockUtil.isLockedInRealtime(this.props.documentEditorLock)
        : false;

      /* Compute final locked state from multiple sources:
       * - this.props.locked: Inherited locked state from parent (includes permissions, version, dirty state)
       * - isLockedToOtherClient: Another user holds the realtime collaborative editor lock
       * - this.props.userReadOnlyMode: User enabled read-only mode via display options
       */
      const locked = this.props.locked || isLockedToOtherClient || Boolean(this.props.userReadOnlyMode);
      if (this.props.note == null) {
        return <div>Loading...</div>;
      } else {
        return (
          <DocumentManagerComponent
            historyManager={this.props.historyManager}
            currentVersion={{
              note: this.props.note,
              structuredTableMappings: this.props.structuredTableMappings,
              title: this.props.title,
            }}
            restoreStateAndSelection={this.props.restoreStateAndSelection}
            renderWith={({
              metadataForRestore,
              storeState,
              restoreState,
              requestSelection,
              undoStoreState,
              undoState,
              redoState,
            }: RenderProps) => (
              <NoteEditorContainer
                afterEntry={this.props.afterEntry}
                banner={this.getBannerMessage()}
                beforeEntry={this.props.beforeEntry}
                cid={this.state.cid}
                cidIdMap={this.props.cidIdMap}
                editorIcon={this.props.editorIcon}
                printHandler={this.props.printHandler}
                title={this.props.title}
                titleBadge={this.props.titleBadge}
                hideTitle={this.props.title == null || this.props.hideTitle === true}
                updateTitle={this.props.updateTitle}
                note={this.props.note!}
                registerObjects={this.props.registerObjects}
                getIdFromCid={this.props.getIdFromCid}
                updateNote={this.props.updateNote}
                fileId={this.props.fileId}
                humanId={this.props.humanId}
                templateVersionId={this.props.templateVersionId}
                templateUrl={this.props.templateUrl}
                templateVersionTimestamp={this.props.templateVersionTimestamp}
                fileType={this.props.fileType as ModelTypeConstants}
                fullScreen={this.props.fullScreen}
                fullHeight={this.props.fullHeight}
                minimalToolbar={this.props.minimalToolbar}
                locked={locked}
                dirty={this.props.dirty}
                lockedFromExternalChange={this.props.lockedFromExternalChange}
                parentType={this.props.parentType}
                printOptions={this.props.printOptions}
                disableFormatting={this.props.disableFormatting}
                disableMaximizedTables={this.props.disableMaximizedTables}
                preview={this.props.preview}
                displayOptions={this.props.displayOptions}
                showTimestamps={this.props.showTimestamps}
                spellCheck={this.props.spellCheck}
                printLayout={this.props.printLayout}
                showTableOfContents={this.props.showTableOfContents}
                showPrintMargins={this.props.showPrintMargins}
                collaboratorIds={this.props.collaboratorIds}
                indexSlice={this.props.indexSlice}
                viewer={this.props.viewer}
                permissions={this.props.permissions}
                isConfig={this.props.isConfig}
                renderCanvas={this.props.renderCanvas}
                renderCanvasTitle={this.props.renderCanvasTitle}
                onLoad={this.props.onLoad}
                // For selection
                storeState={storeState}
                undoStoreState={undoStoreState}
                restoreState={
                  restoreState as unknown as (
                    e: ElementKeyboardEvent,
                    selectionRestoreData:
                      | MetadataForRestore<TableIndex>
                      | {
                          selection: SelectionRestoreData;
                        }
                  ) => boolean
                }
                undoState={undoState}
                redoState={redoState}
                requestSelection={
                  requestSelection as unknown as (
                    selectionRestoreData: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData}
                  ) => void
                }
                metadataForRestore={metadataForRestore}
                focusedTableProvenanceUuid={this.props.focusedTableProvenanceUuid}
                focusedTableViewMode={this.props.focusedTableViewMode}
                tablesToInsertOnCreation={this.props.tablesToInsertOnCreation}
                customInsertableTables={this.props.customInsertableTables}
                isVirtualizationEnabled={this.props.isVirtualizationEnabled}
                isViewingPublishedVersion={this.props.isViewingPublishedVersion}
                tableNamespace={this.props.tableNamespace}
                disableSplitscreen={this.props.disableSplitscreen}
              />
            )}
          />
        );
      }
    }
  }
);

export class EntryNoteView extends React.PureComponent<EntryNoteViewProps, EntryState> {
  // Rather than using an if statement to build up the list, we specify them statically here so that
  // comparisons to check for equality work properly.
  static PRINT_OPTIONS = [PRINT_OPTION.NOTE, PRINT_OPTION.ENTRY, PRINT_OPTION.SELECTION];

  static PRINT_OPTIONS_WITHOUT_PROTOCOLS = [PRINT_OPTION.NOTE, PRINT_OPTION.SELECTION];

  override state: EntryState = {
    // TODO(typescript): once we move this to a functional component, we can likely drop this
    // eslint-disable-next-line react/no-unused-state
    cid: _.uniqueId('note-editor'),
    historyManager: new HistoryManager(),
  };

  restoreStateAndSelection = (
    {note, structuredTableMappings, title}: HistoryState<EntryNote>,
    selectionData: SelectionRestoreData
  ): void => {
    EntryActions.revert(this.props.fileId, {note, structuredTableMappings, name: title}, selectionData);
  };

  printHandler = (tipe: PRINT_OPTION, indexSlice?: IndexSlice | null): void => {
    if (indexSlice == null) {
      indexSlice = null;
    }
    const showFormulas =
      this.props.displayOptions?.getOption(ELN_SHOW_FORMULAS.name)?.get('visible') ?? false;
    switch (tipe) {
      case PRINT_OPTION.NOTE:
        EntryActions.print(this.props.fileId, {
          showNote: true,
          protocolCids: [],
          showTimestamps: this.props.showTimestamps,
          showFormulas,
        });
        break;
      case PRINT_OPTION.ENTRY:
        EntryActions.print(this.props.fileId, {
          showNote: true,
          showTimestamps: this.props.showTimestamps,
          showFormulas,
        });
        break;
      case PRINT_OPTION.SELECTION:
        EntryActions.print(this.props.fileId, {
          showNote: true,
          protocolCids: [],
          indexSlice,
          showTimestamps: this.props.showTimestamps,
          showFormulas,
        });
        break;
      case PRINT_OPTION.TEMPLATE:
        throw new Error(`Unexpected print handler type ${tipe}`);
      default:
        throw new Error(`Unexpected print handler type ${assertNever(tipe)}`);
    }
  };

  updateNote = (
    note: EntryNote,
    opts: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    } = {},
    structuredTableMappings: Array<StructuredTableMapping> | undefined = undefined
  ): void => {
    EntryActions.updateNote(this.props.fileId, note, opts, structuredTableMappings);
  };

  registerObjects = (cidIdMap: CidIdMap): void => {
    EntryActions.registerObjects(this.props.fileId, cidIdMap);
  };

  getIdFromCid = (cid: string): number => EntryStore.getIdFromCid(this.props.fileId, cid);

  updateTitle = (name: string): void => {
    EntryActions.setName(this.props.fileId, name);
  };

  override render(): JSX.Element {
    const locked = !this.props.isLatest || !this.props.allowEdits;
    const enableReviewLifecycle = areReviewLifecyclesEnabledForUser();
    return (
      <NoteView
        {..._.defaults(
          {
            beforeEntry: <RequestEntryTable entryId={this.props.fileId} locked={locked} viewer={false} />,
            getIdFromCid: this.getIdFromCid,
            printHandler: this.printHandler,
            registerObjects: this.registerObjects,
            updateNote: this.updateNote,
            updateTitle: this.updateTitle,
            historyManager: this.state.historyManager,
            locked,
            restoreStateAndSelection: this.restoreStateAndSelection,
            printOptions: this.props.printOptions,
            collaboratorIds: this.props.collaboratorIds,
            titleBadge: (
              <EntryNoteTitleBadge
                hasEnterprisePlan={licenses.hasEnterprisePlan()}
                entryId={this.props.fileId}
                enableReviewLifecycle={enableReviewLifecycle}
              />
            ),
            permissions: this.props.permissions,
            editorIcon: (
              <Tooltip text="Entry">
                <BenchlingIcon icon="entry" />
              </Tooltip>
            ),
          },
          this.props
        )}
      />
    );
  }
}
